import type { ReactNode } from "react";
import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the workplace-policies start screen. Three
 * short slides (what/who, what to watch for, key terms) shown in the reader
 * while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. film + policy site: the film introduces, the policy is the rule  */
/* ------------------------------------------------------------------ */
function PolicyRow({ y, title, done, pulse }: { y: number; title: string; done: boolean; pulse?: boolean }): JSX.Element {
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="25" rx="7" fill={C.panel2} stroke={C.line} />
      {done ? (
        <g>
          <circle cx="32" cy={y + 13} r="7" fill={C.mint} fillOpacity="0.16" />
          <polyline points={`28.5,${y + 13} 31,${y + 15.6} 35.5,${y + 10.2}`} fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ) : (
        <circle cx="32" cy={y + 13} r="6.5" fill="none" stroke={C.muted} strokeWidth="1.3" />
      )}
      <text x="48" y={y + 17} fontSize="12" fill={C.text}>
        {title}
      </text>
      {done ? (
        <text x="330" y={y + 17} fontSize="11" fontWeight="700" fill={C.mint} textAnchor="end">
          Acknowledged
        </text>
      ) : (
        <g>
          <rect
            className={pulse ? "sr-anim-pulse" : undefined}
            x="286"
            y={y + 5}
            width="44"
            height="16"
            rx="8"
            fill={pulse ? C.amber : C.panel}
            fillOpacity={pulse ? 0.14 : 1}
            stroke={pulse ? C.amber : C.line}
            strokeOpacity={pulse ? 0.5 : 1}
          />
          <text x="308" y={y + 16.6} fontSize="10.5" fontWeight="700" fill={pulse ? C.amber : C.muted} textAnchor="middle">
            Open
          </text>
        </g>
      )}
    </g>
  );
}

function FilmPolicyArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: briefing film and policy site" aria="A short briefing film, then a list of company policies where two are acknowledged and two are still to read" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Company policies
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="76" rx="8" fill={C.code} stroke={C.line} />
      <circle className="sr-anim-ring" cx="46" cy="110" r="14" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <circle cx="46" cy="110" r="14" fill={C.mint} fillOpacity="0.14" />
      <path d="M42 103.5 L53 110 L42 116.5 z" fill={C.mint} />
      <text x="72" y="106" fontSize="12.5" fontWeight="700" fill={C.text}>
        Briefing film
      </text>
      <text x="72" y="122" fontSize="10.5" fill={C.muted}>
        Introduces the policies
      </text>
      <rect x="30" y="138" width="298" height="4" rx="2" fill={C.line} />
      <rect x="30" y="138" width="120" height="4" rx="2" fill={C.mint} />

      <text x="18" y="177" fontSize="10.5" fill={C.muted}>
        Then read the policies themselves
      </text>
      <PolicyRow y={186} title="Secure internet usage" done />
      <PolicyRow y={216} title="Secure password usage" done />
      <PolicyRow y={246} title="Company devices" done={false} pulse />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. what to watch for: lookalike login, unexpected MFA, unknown USB  */
/* ------------------------------------------------------------------ */
function WatchRow({ y, label, verdict, tone, children }: { y: number; label: string; verdict: string; tone: string; children: ReactNode }): JSX.Element {
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="77" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="30" y={y + 18} fontSize="10.5" fill={C.muted}>
        {label}
      </text>
      {children}
      <circle cx="33" cy={y + 65} r="3" fill={tone} />
      <text x="42" y={y + 69} fontSize="11.5" fontWeight="700" fill={tone}>
        {verdict}
      </text>
    </g>
  );
}

function WatchArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label="Example: three things to stop on" aria="Three examples: a lookalike login address, an unexpected sign-in approval prompt, and an unknown USB drive" height={H}>
      <WatchRow y={34} label="Website" verdict="Lookalike address. Do not sign in." tone={C.red}>
        <rect x="30" y="60" width="300" height="26" rx="13" fill={C.code} stroke={C.line} />
        <rect x="42" y="69.5" width="9" height="7" rx="1.5" fill="none" stroke={C.muted} strokeWidth="1.3" />
        <text x="58" y="77" fontSize="11" fontFamily={MONO} fill={C.red}>
          https://login.c0mpany-portal.net
        </text>
        <circle className="sr-anim-ring" cx="162" cy="73" r="7" fill="none" stroke={C.red} strokeWidth="1.5" />
      </WatchRow>

      <WatchRow y={120} label="Sign-in prompt" verdict="You did not start this. Deny it." tone={C.amber}>
        <text x="30" y="170" fontSize="13" fontWeight="700" fill={C.text}>
          Approve sign-in?
        </text>
        <rect x="222" y="152" width="52" height="24" rx="12" fill={C.panel} stroke={C.line} />
        <text x="248" y="168" fontSize="11" fill={C.muted} textAnchor="middle">
          Approve
        </text>
        <rect className="sr-anim-pulse" x="282" y="152" width="48" height="24" rx="12" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" />
        <text x="306" y="168" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
          Deny
        </text>
      </WatchRow>

      <WatchRow y={206} label="Removable drive" verdict="Not issued by IT. Do not plug in." tone={C.amber}>
        <g className="sr-anim-float">
          <rect x="30" y="248" width="14" height="14" rx="2" fill="none" stroke={C.soft} strokeWidth="1.4" />
          <rect x="44" y="244" width="40" height="22" rx="4" fill={C.code} stroke={C.soft} strokeWidth="1.4" />
          <rect x="50" y="251" width="14" height="8" rx="1.5" fill={C.amber} fillOpacity="0.3" />
        </g>
        <text x="96" y="259" fontSize="11" fontFamily={MONO} fill={C.soft}>
          UNKNOWN_DRIVE
        </text>
      </WatchRow>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. report it: message to IT, three steps, report even if unsure     */
/* ------------------------------------------------------------------ */
function Step({ x, n, line1, line2 }: { x: number; n: string; line1: string; line2: string }): JSX.Element {
  return (
    <g transform={`translate(${x} 196)`}>
      <rect x="0.5" y="0.5" width="103" height="52" rx="9" fill={C.panel2} stroke={C.line} />
      <circle cx="16" cy="16" r="9" fill={C.mint} fillOpacity="0.14" />
      <text x="16" y="20" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
        {n}
      </text>
      <text x="32" y="20" fontSize="11.5" fontWeight="700" fill={C.text}>
        {line1}
      </text>
      <text x="10" y="40" fontSize="10.5" fill={C.muted}>
        {line2}
      </text>
    </g>
  );
}

function ReportArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: report to IT" aria="A short message to the IT security team, and three steps: stop, do not investigate, report" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Report an incident
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="104" rx="8" fill={C.code} stroke={C.line} />
      <text x="30" y="99" fontSize="10.5" fill={C.muted}>
        To
      </text>
      <text x="52" y="99" fontSize="11.5" fill={C.text}>
        IT / security team
      </text>
      <line x1="17" y1="109.5" x2="343" y2="109.5" stroke={C.line} />
      <text x="30" y="127" fontSize="12" fontWeight="700" fill={C.text}>
        Suspicious email, did not click
      </text>
      <rect x="30" y="137" width="250" height="5" rx="2.5" fill={C.line} />
      <rect x="30" y="150" width="200" height="5" rx="2.5" fill={C.line} />
      <rect x="30" y="163" width="130" height="5" rx="2.5" fill={C.line} />
      <rect className="sr-anim-blink" x="166" y="160" width="1.5" height="11" rx="0.75" fill={C.mint} />
      <rect className="sr-anim-pulse" x="278" y="152" width="52" height="22" rx="11" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" />
      <text x="304" y="167" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
        Send
      </text>

      <Step x={16} n="1" line1="Stop" line2="Do not click" />
      <Step x={128} n="2" line1="Hands off" line2="Do not fix it" />
      <Step x={240} n="3" line1="Report" line2="Tell IT now" />

      <rect x="16.5" y="262.5" width="327" height="24" rx="8" fill={C.mint} fillOpacity="0.08" stroke={C.mint} strokeOpacity="0.35" />
      <text x="180" y="279" fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        Not sure it counts? Report it anyway.
      </text>
    </Frame>
  );
}

export const WORKPLACE_POLICIES_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Know the rules. Then follow them.",
    illustration: FilmPolicyArt,
    caption: "The film introduces. The policy is the rule.",
    sections: [
      {
        h: "What this is about",
        body: "At your desk you watch a short film, open the company policy site, read each policy, acknowledge it, and answer a short quiz.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who uses company systems, devices or accounts. No technical background needed.",
      },
      {
        h: "Why you are the control",
        body: "A policy only works when people apply it. No tool can tell that you are in a hurry, or that a shortcut breaks a rule. That call is yours.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Where good habits slip.",
    illustration: WatchArt,
    caption: "When something is off, stop and check the policy.",
    sections: [
      {
        h: "Lookalike websites",
        body: "A login page can copy a real one. Check the address before you type anything, and report a site you doubt.",
      },
      {
        h: "Weak or shared passwords",
        body: "Use a unique password for each account, keep it in the approved manager, and never share it.",
      },
      {
        h: "Unapproved software and drives",
        body: "Installing unapproved software or plugging in a personal drive can bring malware onto the network. Ask IT first.",
      },
      {
        h: "Unexpected prompts",
        body: "A sign-in approval you did not start is an attack in progress. Deny it and report it.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "Report early, report anyway.",
    illustration: ReportArt,
    caption: "Quick reporting limits the damage.",
    sections: [
      { h: "Policy", body: "A rule your company publishes. The document is the rule, not a film or a colleague\u2019s memory." },
      { h: "Acknowledge", body: "Confirm that you have read a policy and will apply it. Do it after reading, not before." },
      { h: "Security incident", body: "Anything suspicious that touches your data, accounts or devices: a phishing email, malware, unauthorised access." },
      { h: "Report", body: "Tell IT with the time and any screenshots. Do not investigate or fix it yourself. If unsure, report it." },
    ],
  },
];
